// ── useProductModal.js ────────────────────────────────────────────────────────
// Central state management and all action handlers for ProductDetailModal
// ✅ FIX: isFollowLoading forwarded from useFollowSeller (was hardcoded false)

import { useState, useEffect } from 'react';
import { useAuthContext } from '../../../../context/AuthContext';
import apiService from '../../../../services/api';
import useAuthentication from './useAuthentication';
import useFollowSeller from './useFollowSeller';

const useProductModal = ({ product, isLoggedIn, isOpen, onOpenLoginModal, onProductClick }) => {
  const { checkAuthentication }                             = useAuthentication();
  const { profileImage: authProfileImage }                  = useAuthContext();
  const { isFollowing, followCount, setFollowCount,
          checkIfFollowing, handleFollowToggle,
          isFollowLoading }                                 = useFollowSeller({ product, onOpenLoginModal }); // ✅ FIX

  // ── Image state ───────────────────────────────────────────────────────────
  const [activeImageIndex, setActiveImageIndex]             = useState(0);

  // ── UI toggles ────────────────────────────────────────────────────────────
  const [showSellerInfo, setShowSellerInfo]                 = useState(false);
  const [showCopyNotification, setShowCopyNotification]     = useState(false);

  // ── Modal visibility ──────────────────────────────────────────────────────
  const [showOfferModal, setShowOfferModal]                 = useState(false);
  const [showMessageModal, setShowMessageModal]             = useState(false);
  const [showRatingModal, setShowRatingModal]               = useState(false);

  // ── Like state ────────────────────────────────────────────────────────────
  const [likes, setLikes]                                   = useState(product?.like_count || product?.likes || 0);
  const [isLiked, setIsLiked]                               = useState(product?.is_liked || false);
  const [isLiking, setIsLiking]                             = useState(false);

  // ── Offer state ───────────────────────────────────────────────────────────
  const [offerAmount, setOfferAmount]                       = useState('');
  const [offerMessage, setOfferMessage]                     = useState('');
  const [isSubmittingOffer, setIsSubmittingOffer]           = useState(false);
  const [offerError, setOfferError]                         = useState('');

  // ── Message state ─────────────────────────────────────────────────────────
  const [messageText, setMessageText]                       = useState('');
  const [isSubmittingMessage, setIsSubmittingMessage]       = useState(false);
  const [messageError, setMessageError]                     = useState('');

  // ── Rating state ──────────────────────────────────────────────────────────
  const [userRating, setUserRating]                         = useState(0);
  const [hoverRating, setHoverRating]                       = useState(0);
  const [ratingComment, setRatingComment]                   = useState('');
  const [isSubmittingRating, setIsSubmittingRating]         = useState(false);
  const [ratingSuccess, setRatingSuccess]                   = useState('');
  const [ratingError, setRatingError]                       = useState('');

  // ── Report state ──────────────────────────────────────────────────────────
  const [showReportModal, setShowReportModal]               = useState(false);
  const [reportReason, setReportReason]                     = useState('');
  const [reportDetails, setReportDetails]                   = useState('');
  const [isSubmittingReport, setIsSubmittingReport]         = useState(false);
  const [reportSuccess, setReportSuccess]                   = useState('');
  const [reportError, setReportError]                       = useState('');

  // ── User identity ─────────────────────────────────────────────────────────
  const [sellerAvatarUrl, setSellerAvatarUrl]               = useState(null);
  const [loggedInUser, setLoggedInUser]                     = useState(null);

  // ── Load logged-in user identity from localStorage (Follow button etc.) ───
  // ✅ FIX: This only sets loggedInUser identity — NOT sellerAvatarUrl.
  //    sellerAvatarUrl must come from the SELLER's profile, not the viewer's.
  useEffect(() => {
    try {
      const rawUser =
        localStorage.getItem('user') ||
        localStorage.getItem('userData') ||
        localStorage.getItem('userProfile') ||
        localStorage.getItem('currentUser');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        const identity =
          parsed?.email    || parsed?.username ||
          parsed?.name     || parsed?.id       || parsed?.userId || null;
        setLoggedInUser(identity);
      }
    } catch (e) {
      console.warn('Could not parse user profile:', e);
    }
  }, [isLoggedIn]);

  // ── Resolve the SELLER's profile image (not the logged-in user's avatar) ──
  // Priority 1: product already carries seller_profile_image_url (fast — no extra call)
  //             This works once the updated serializer is deployed.
  // Priority 2: Fallback to public getSellerProfile() endpoint.
  // Runs on product.id change so stale avatars never bleed between products.
  const _resolveAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    return `https://imselling-marketplace-production.up.railway.app${url.startsWith('/') ? '' : '/'}${url}`;
  };

  useEffect(() => {
    if (!product) { setSellerAvatarUrl(null); return; }

    const resolveAvatar = async () => {
      // ── If the logged-in user IS the seller, always use AuthContext image ──
      const loggedInUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
      })();
      const loggedInEmail = (loggedInUser?.email || loggedInUser?.identifier || '').toLowerCase().trim();
      const sellerEmail   = (product?.email || product?.seller_email || '').toLowerCase().trim();

      if (loggedInEmail && sellerEmail && loggedInEmail === sellerEmail) {
        if (authProfileImage) { setSellerAvatarUrl(authProfileImage); return; }
        return; // wait for authProfileImage — effect re-runs when it hydrates
      }

      // ── Other sellers: use product-embedded field ─────────────────────────
      const fromProduct =
        _resolveAvatarUrl(product?.seller_profile_image_url) ||
        _resolveAvatarUrl(product?.seller_avatar_url)        ||
        _resolveAvatarUrl(product?.seller_profile_image)     ||
        _resolveAvatarUrl(product?.seller_avatar)            ||
        null;
      if (fromProduct) { setSellerAvatarUrl(fromProduct); return; }

      // ── Fallback: public seller-profile API ───────────────────────────────
      const sellerEmailForApi =
        product?.email        ||
        product?.seller_email ||
        product?.contact_email || null;

      if (sellerEmailForApi) {
        try {
          const data = await apiService.getSellerProfile?.(sellerEmailForApi);
          const url = _resolveAvatarUrl(data?.profile_image_url || data?.profile_image);
          if (url) { setSellerAvatarUrl(url); return; }
        } catch (_) { /* ignore */ }
      }

      setSellerAvatarUrl(null);
    };

    resolveAvatar();
  }, [product?.id, authProfileImage]);

  // ── Reset all state when product changes ──────────────────────────────────
  useEffect(() => {
    if (product) {
      setActiveImageIndex(0);
      setShowSellerInfo(false);
      setLikes(product.like_count || product.likes || 0);
      setIsLiked(product.is_liked || false);
      setOfferAmount('');
      setOfferMessage('');
      setMessageText('');
      setShowOfferModal(false);
      setShowMessageModal(false);
      setShowRatingModal(false);
      setUserRating(0);
      setHoverRating(0);
      setRatingComment('');
      setRatingSuccess('');
      setRatingError('');
      setMessageError('');
      setOfferError('');
      setShowReportModal(false);
      setReportReason('');
      setReportDetails('');
      setReportSuccess('');
      setReportError('');
      checkIfFollowing(product);
    }
  }, [product?.id]);

  // ── Restore pending product modal after login ──────────────────────────────
  useEffect(() => {
    const handleRestore = () => {
      try {
        const savedProduct = localStorage.getItem('pendingProductView');
        if (savedProduct) {
          const { productId, showSellerInfo: shouldShowSeller } = JSON.parse(savedProduct);
          if (product && product.id === productId) setShowSellerInfo(shouldShowSeller);
          localStorage.removeItem('pendingProductView');
        }
      } catch (error) {
        console.error('Error restoring product modal:', error);
      }
    };
    window.addEventListener('restoreProductModal', handleRestore);
    return () => window.removeEventListener('restoreProductModal', handleRestore);
  }, [product, onProductClick]);

  useEffect(() => {
    if (isOpen && product && isLoggedIn) {
      try {
        const savedProduct = localStorage.getItem('pendingProductView');
        if (savedProduct) {
          const { productId, showSellerInfo: shouldShowSeller } = JSON.parse(savedProduct);
          if (productId === product.id && shouldShowSeller) {
            setShowSellerInfo(true);
            localStorage.removeItem('pendingProductView');
          }
        }
      } catch (error) {
        console.error('Error checking pending product:', error);
      }
    }
  }, [isLoggedIn, isOpen, product]);

  // ── Update URL when product opens so the tab URL is shareable ─────────────
  useEffect(() => {
    if (isOpen && product?.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('ad', product.id);
      window.history.replaceState({}, '', url.toString());
    } else if (!isOpen) {
      // Remove ?ad=... when modal closes
      const url = new URL(window.location.href);
      url.searchParams.delete('ad');
      window.history.replaceState({}, '', url.toString());
    }
  }, [isOpen, product?.id]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleRelatedProductClick = (relatedProduct) => {
    setActiveImageIndex(0);
    setShowSellerInfo(false);
    if (onProductClick) onProductClick(relatedProduct);
  };

  const handleContactSellerClick = () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      const productState = { productId: product.id, showSellerInfo: true, timestamp: Date.now() };
      localStorage.setItem('pendingProductView', JSON.stringify(productState));
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to view seller contact information');
    } else {
      setShowSellerInfo(!showSellerInfo);
    }
  };

  const handleLikeToggle = async () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to like this product');
      return;
    }
    if (isLiking) return;
    setIsLiking(true);

    const optimisticLiked = !isLiked;
    const optimisticCount = optimisticLiked ? likes + 1 : Math.max(0, likes - 1);
    setIsLiked(optimisticLiked);
    setLikes(optimisticCount);

    try {
      const res = await apiService.toggleAdLike(product.id);
      if (res.success) {
        setIsLiked(res.is_liked);
        setLikes(res.like_count);
      } else {
        setIsLiked(!optimisticLiked);
        setLikes(likes);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      setIsLiked(!optimisticLiked);
      setLikes(likes);
    } finally {
      setIsLiking(false);
    }
  };

  const handleMakeOffer = () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to make an offer');
      return;
    }
    setOfferError('');
    setShowOfferModal(true);
  };

  const handleSubmitOffer = async () => {
    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      setOfferError('Please enter a valid offer amount');
      return;
    }
    if (parseFloat(offerAmount) > product.price) {
      const confirmProceed = window.confirm(
        `Your offer (KES ${parseFloat(offerAmount).toLocaleString()}) is higher than the asking price (KES ${product.price.toLocaleString()}). Proceed?`
      );
      if (!confirmProceed) return;
    }
    try {
      setIsSubmittingOffer(true);
      setOfferError('');
      const payload = { amount: parseFloat(offerAmount), message: offerMessage.trim() || '' };
      const response = await apiService.createOffer(product.id, payload);
      if (response.success) {
        alert(response.message || `Offer of KES ${parseFloat(offerAmount).toLocaleString()} submitted!`);
        setShowOfferModal(false);
        setOfferAmount('');
        setOfferMessage('');
      } else {
        setOfferError(response.error || 'Failed to submit offer. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
      setOfferError('An error occurred. Please try again.');
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const handleMessageSeller = () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to message the seller');
      return;
    }
    setMessageError('');
    setShowMessageModal(true);
  };

  const handleSubmitMessage = async () => {
    if (!messageText.trim()) {
      setMessageError('Please enter a message');
      return;
    }
    try {
      setIsSubmittingMessage(true);
      setMessageError('');
      const payload = { message: messageText.trim() };
      let response;
      try {
        response = await apiService.sendMessage(product.id, payload);
      } catch (firstError) {
        console.warn('First attempt failed, trying alternative:', firstError);
        if (typeof apiService.createMessage === 'function') {
          response = await apiService.createMessage(product.id, payload);
        } else if (typeof apiService.postMessage === 'function') {
          response = await apiService.postMessage(product.id, payload);
        } else {
          throw firstError;
        }
      }
      if (response && response.success) {
        setShowMessageModal(false);
        setMessageText('');
        alert(response.message || 'Message sent successfully!');
      } else {
        setMessageError(
          response?.error || response?.detail || response?.message ||
          'Failed to send message. Please check your connection and try again.'
        );
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessageError(
        error?.response?.data?.error || error?.response?.data?.detail ||
        error?.message || 'Failed to send message. Please try again.'
      );
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  // ✅ FIX: Copy the actual current tab URL (which now contains ?ad=ID)
  // so recipients land directly on this product, not the home page.
  const handleCopyLink = () => {
    const productUrl = window.location.href;
    navigator.clipboard.writeText(productUrl).then(() => {
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 2500);
    }).catch(() => {
      // Fallback for browsers that block clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = productUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), 2500);
    });
  };

  const handleSaveToWishlist = () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to save to wishlist');
      return;
    }
    try {
      const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      if (!wishlist.includes(product.id)) {
        wishlist.push(product.id);
        localStorage.setItem('wishlist', JSON.stringify(wishlist));
        alert('Added to wishlist!');
      } else {
        alert('Already in wishlist!');
      }
    } catch (error) {
      console.error('Error saving to wishlist:', error);
    }
  };

  const handleOpenRating = () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to rate this seller');
      return;
    }
    setRatingSuccess('');
    setRatingError('');
    setShowRatingModal(true);
  };

  const handleSubmitRating = async () => {
    if (userRating === 0) {
      setRatingError('Please select a star rating');
      return;
    }
    try {
      setIsSubmittingRating(true);
      setRatingError('');
      const payload = { rating: userRating, comment: ratingComment.trim() };
      let response;
      if (typeof apiService.rateSeller === 'function') {
        response = await apiService.rateSeller(product.seller, payload);
      } else if (typeof apiService.submitRating === 'function') {
        response = await apiService.submitRating(product.id, payload);
      } else if (typeof apiService.createRating === 'function') {
        response = await apiService.createRating(product.id, payload);
      } else {
        const auth = checkAuthentication();
        const res  = await fetch(`/api/ads/${product.id}/rate/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
          body: JSON.stringify(payload),
        });
        response = await res.json();
      }
      if (response && (response.success || response.id || response.rating)) {
        setRatingSuccess(`Thank you! You rated this seller ${userRating} star${userRating > 1 ? 's' : ''}.`);
        setTimeout(() => {
          setShowRatingModal(false);
          setUserRating(0);
          setRatingComment('');
          setRatingSuccess('');
        }, 2000);
      } else {
        setRatingError(response?.error || response?.detail || 'Failed to submit rating. Try again.');
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      setRatingError(error?.message || 'Failed to submit rating. Please try again.');
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleOpenReport = () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to report a seller');
      return;
    }
    setReportSuccess('');
    setReportError('');
    setReportReason('');
    setReportDetails('');
    setShowReportModal(true);
  };

  const handleSubmitReport = async () => {
    if (!reportReason) {
      setReportError('Please select a reason for reporting');
      return;
    }
    try {
      setIsSubmittingReport(true);
      setReportError('');
      const payload = {
        reason: reportReason,
        details: reportDetails.trim(),
        seller: product.seller,
        ad_id: product.id,
      };
      const response = await apiService.reportSeller(product.id, payload);
      if (response.success) {
        setReportSuccess('Report submitted successfully. Our team will review it shortly.');
        setTimeout(() => {
          setShowReportModal(false);
          setReportReason('');
          setReportDetails('');
          setReportSuccess('');
        }, 2500);
      } else {
        setReportError(response.error || 'Failed to submit report. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      setReportError('An error occurred. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  return {
    // Image
    activeImageIndex, setActiveImageIndex,
    // UI
    showSellerInfo, showCopyNotification,
    // Modals
    showOfferModal, setShowOfferModal,
    showMessageModal, setShowMessageModal,
    showRatingModal, setShowRatingModal,
    showReportModal, setShowReportModal,
    // Like
    likes, isLiked, isLiking,
    // Offer
    offerAmount, setOfferAmount,
    offerMessage, setOfferMessage,
    isSubmittingOffer, offerError, setOfferError,
    // Message
    messageText, setMessageText,
    isSubmittingMessage, messageError, setMessageError,
    // Rating
    userRating, setUserRating,
    hoverRating, setHoverRating,
    ratingComment, setRatingComment,
    isSubmittingRating, ratingSuccess, ratingError, setRatingError,
    // Report
    reportReason, setReportReason,
    reportDetails, setReportDetails,
    isSubmittingReport, reportSuccess, reportError, setReportError,
    // User
    sellerAvatarUrl, loggedInUser, authProfileImage,
    // Follow
    isFollowing, followCount,
    isFollowLoading, // ✅ FIX: real value from useFollowSeller, not hardcoded false
    // Handlers
    handleRelatedProductClick,
    handleContactSellerClick,
    handleLikeToggle,
    handleMakeOffer,
    handleSubmitOffer,
    handleMessageSeller,
    handleSubmitMessage,
    handleCopyLink,
    handleSaveToWishlist,
    handleOpenRating,
    handleSubmitRating,
    handleFollowToggle,
    handleOpenReport,
    handleSubmitReport,
    checkAuthentication,
  };
};

export default useProductModal;

















// // ── useProductModal.js ────────────────────────────────────────────────────────
// // Central state management and all action handlers for ProductDetailModal

// import { useState, useEffect } from 'react';
// import apiService from '../../../../services/api';
// import useAuthentication from './useAuthentication';
// import useFollowSeller from './useFollowSeller';

// const useProductModal = ({ product, isLoggedIn, isOpen, onOpenLoginModal, onProductClick }) => {
//   const { checkAuthentication }                             = useAuthentication();
//   const { isFollowing, followCount, setFollowCount,
//           checkIfFollowing, handleFollowToggle }            = useFollowSeller({ product, onOpenLoginModal });

//   // ── Image state ───────────────────────────────────────────────────────────
//   const [activeImageIndex, setActiveImageIndex]             = useState(0);

//   // ── UI toggles ────────────────────────────────────────────────────────────
//   const [showSellerInfo, setShowSellerInfo]                 = useState(false);
//   const [showCopyNotification, setShowCopyNotification]     = useState(false);

//   // ── Modal visibility ──────────────────────────────────────────────────────
//   const [showOfferModal, setShowOfferModal]                 = useState(false);
//   const [showMessageModal, setShowMessageModal]             = useState(false);
//   const [showRatingModal, setShowRatingModal]               = useState(false);

//   // ── Like state ────────────────────────────────────────────────────────────
//   const [likes, setLikes]                                   = useState(product?.like_count || product?.likes || 0);
//   const [isLiked, setIsLiked]                               = useState(product?.is_liked || false);
//   const [isLiking, setIsLiking]                             = useState(false);

//   // ── Offer state ───────────────────────────────────────────────────────────
//   const [offerAmount, setOfferAmount]                       = useState('');
//   const [offerMessage, setOfferMessage]                     = useState('');
//   const [isSubmittingOffer, setIsSubmittingOffer]           = useState(false);
//   const [offerError, setOfferError]                         = useState('');

//   // ── Message state ─────────────────────────────────────────────────────────
//   const [messageText, setMessageText]                       = useState('');
//   const [isSubmittingMessage, setIsSubmittingMessage]       = useState(false);
//   const [messageError, setMessageError]                     = useState('');

//   // ── Rating state ──────────────────────────────────────────────────────────
//   const [userRating, setUserRating]                         = useState(0);
//   const [hoverRating, setHoverRating]                       = useState(0);
//   const [ratingComment, setRatingComment]                   = useState('');
//   const [isSubmittingRating, setIsSubmittingRating]         = useState(false);
//   const [ratingSuccess, setRatingSuccess]                   = useState('');
//   const [ratingError, setRatingError]                       = useState('');

//   // ── Report state ──────────────────────────────────────────────────────────
//   const [showReportModal, setShowReportModal]               = useState(false);
//   const [reportReason, setReportReason]                     = useState('');
//   const [reportDetails, setReportDetails]                   = useState('');
//   const [isSubmittingReport, setIsSubmittingReport]         = useState(false);
//   const [reportSuccess, setReportSuccess]                   = useState('');
//   const [reportError, setReportError]                       = useState('');

//   // ── User identity ─────────────────────────────────────────────────────────
//   const [sellerAvatarUrl, setSellerAvatarUrl]               = useState(null);
//   const [loggedInUser, setLoggedInUser]                     = useState(null);

//   // ── Load user from localStorage ───────────────────────────────────────────
//   useEffect(() => {
//     try {
//       const rawUser =
//         localStorage.getItem('user') ||
//         localStorage.getItem('userData') ||
//         localStorage.getItem('userProfile') ||
//         localStorage.getItem('currentUser');
//       if (rawUser) {
//         const parsed = JSON.parse(rawUser);
//         const avatar =
//           parsed?.avatar         || parsed?.profileImage    ||
//           parsed?.profile_picture|| parsed?.photo           ||
//           parsed?.picture        || parsed?.profile?.avatar ||
//           parsed?.profile?.image || null;
//         setSellerAvatarUrl(avatar);
//         const identity =
//           parsed?.email    || parsed?.username ||
//           parsed?.name     || parsed?.id       || parsed?.userId || null;
//         setLoggedInUser(identity);
//       }
//     } catch (e) {
//       console.warn('Could not parse user profile:', e);
//     }
//   }, [isLoggedIn]);

//   // ── Reset all state when product changes ──────────────────────────────────
//   useEffect(() => {
//     if (product) {
//       setActiveImageIndex(0);
//       setShowSellerInfo(false);
//       setLikes(product.like_count || product.likes || 0);
//       setIsLiked(product.is_liked || false);
//       setOfferAmount('');
//       setOfferMessage('');
//       setMessageText('');
//       setShowOfferModal(false);
//       setShowMessageModal(false);
//       setShowRatingModal(false);
//       setUserRating(0);
//       setHoverRating(0);
//       setRatingComment('');
//       setRatingSuccess('');
//       setRatingError('');
//       setMessageError('');
//       setOfferError('');
//       // Reset report states
//       setShowReportModal(false);
//       setReportReason('');
//       setReportDetails('');
//       setReportSuccess('');
//       setReportError('');
//       checkIfFollowing(product);
//     }
//   }, [product?.id]);

//   // ── Restore pending product modal after login ──────────────────────────────
//   useEffect(() => {
//     const handleRestore = () => {
//       try {
//         const savedProduct = localStorage.getItem('pendingProductView');
//         if (savedProduct) {
//           const { productId, showSellerInfo: shouldShowSeller } = JSON.parse(savedProduct);
//           if (product && product.id === productId) setShowSellerInfo(shouldShowSeller);
//           localStorage.removeItem('pendingProductView');
//         }
//       } catch (error) {
//         console.error('Error restoring product modal:', error);
//       }
//     };
//     window.addEventListener('restoreProductModal', handleRestore);
//     return () => window.removeEventListener('restoreProductModal', handleRestore);
//   }, [product, onProductClick]);

//   useEffect(() => {
//     if (isOpen && product && isLoggedIn) {
//       try {
//         const savedProduct = localStorage.getItem('pendingProductView');
//         if (savedProduct) {
//           const { productId, showSellerInfo: shouldShowSeller } = JSON.parse(savedProduct);
//           if (productId === product.id && shouldShowSeller) {
//             setShowSellerInfo(true);
//             localStorage.removeItem('pendingProductView');
//           }
//         }
//       } catch (error) {
//         console.error('Error checking pending product:', error);
//       }
//     }
//   }, [isLoggedIn, isOpen, product]);

//   // ── Handlers ───────────────────────────────────────────────────────────────

//   const handleRelatedProductClick = (relatedProduct) => {
//     setActiveImageIndex(0);
//     setShowSellerInfo(false);
//     if (onProductClick) onProductClick(relatedProduct);
//   };

//   const handleContactSellerClick = () => {
//     const auth = checkAuthentication();
//     if (!auth.isAuthenticated) {
//       const productState = { productId: product.id, showSellerInfo: true, timestamp: Date.now() };
//       localStorage.setItem('pendingProductView', JSON.stringify(productState));
//       if (onOpenLoginModal) onOpenLoginModal();
//       else alert('Please log in to view seller contact information');
//     } else {
//       setShowSellerInfo(!showSellerInfo);
//     }
//   };

//   const handleLikeToggle = async () => {
//     const auth = checkAuthentication();
//     if (!auth.isAuthenticated) {
//       if (onOpenLoginModal) onOpenLoginModal();
//       else alert('Please log in to like this product');
//       return;
//     }
//     if (isLiking) return;
//     setIsLiking(true);

//     const optimisticLiked = !isLiked;
//     const optimisticCount = optimisticLiked ? likes + 1 : Math.max(0, likes - 1);
//     setIsLiked(optimisticLiked);
//     setLikes(optimisticCount);

//     try {
//       const res = await apiService.toggleAdLike(product.id);
//       if (res.success) {
//         setIsLiked(res.is_liked);
//         setLikes(res.like_count);
//       } else {
//         setIsLiked(!optimisticLiked);
//         setLikes(likes);
//       }
//     } catch (error) {
//       console.error('Error toggling like:', error);
//       setIsLiked(!optimisticLiked);
//       setLikes(likes);
//     } finally {
//       setIsLiking(false);
//     }
//   };

//   const handleMakeOffer = () => {
//     const auth = checkAuthentication();
//     if (!auth.isAuthenticated) {
//       if (onOpenLoginModal) onOpenLoginModal();
//       else alert('Please log in to make an offer');
//       return;
//     }
//     setOfferError('');
//     setShowOfferModal(true);
//   };

//   const handleSubmitOffer = async () => {
//     if (!offerAmount || parseFloat(offerAmount) <= 0) {
//       setOfferError('Please enter a valid offer amount');
//       return;
//     }
//     if (parseFloat(offerAmount) > product.price) {
//       const confirmProceed = window.confirm(
//         `Your offer (KES ${parseFloat(offerAmount).toLocaleString()}) is higher than the asking price (KES ${product.price.toLocaleString()}). Proceed?`
//       );
//       if (!confirmProceed) return;
//     }
//     try {
//       setIsSubmittingOffer(true);
//       setOfferError('');
//       const payload = { amount: parseFloat(offerAmount), message: offerMessage.trim() || '' };
//       console.log('Submitting offer payload:', payload);
//       const response = await apiService.createOffer(product.id, payload);
//       if (response.success) {
//         alert(response.message || `Offer of KES ${parseFloat(offerAmount).toLocaleString()} submitted!`);
//         setShowOfferModal(false);
//         setOfferAmount('');
//         setOfferMessage('');
//       } else {
//         setOfferError(response.error || 'Failed to submit offer. Please try again.');
//       }
//     } catch (error) {
//       console.error('Error submitting offer:', error);
//       setOfferError('An error occurred. Please try again.');
//     } finally {
//       setIsSubmittingOffer(false);
//     }
//   };

//   const handleMessageSeller = () => {
//     const auth = checkAuthentication();
//     if (!auth.isAuthenticated) {
//       if (onOpenLoginModal) onOpenLoginModal();
//       else alert('Please log in to message the seller');
//       return;
//     }
//     setMessageError('');
//     setShowMessageModal(true);
//   };

//   const handleSubmitMessage = async () => {
//     if (!messageText.trim()) {
//       setMessageError('Please enter a message');
//       return;
//     }
//     try {
//       setIsSubmittingMessage(true);
//       setMessageError('');
//       const payload = { message: messageText.trim() };
//       console.log('Sending message payload:', payload);
//       let response;
//       try {
//         response = await apiService.sendMessage(product.id, payload);
//       } catch (firstError) {
//         console.warn('First attempt failed, trying alternative:', firstError);
//         if (typeof apiService.createMessage === 'function') {
//           response = await apiService.createMessage(product.id, payload);
//         } else if (typeof apiService.postMessage === 'function') {
//           response = await apiService.postMessage(product.id, payload);
//         } else {
//           throw firstError;
//         }
//       }
//       if (response && response.success) {
//         setShowMessageModal(false);
//         setMessageText('');
//         alert(response.message || 'Message sent successfully!');
//       } else {
//         setMessageError(
//           response?.error || response?.detail || response?.message ||
//           'Failed to send message. Please check your connection and try again.'
//         );
//       }
//     } catch (error) {
//       console.error('Error sending message:', error);
//       setMessageError(
//         error?.response?.data?.error || error?.response?.data?.detail ||
//         error?.message || 'Failed to send message. Please try again.'
//       );
//     } finally {
//       setIsSubmittingMessage(false);
//     }
//   };

//   const handleCopyLink = () => {
//     const productUrl = `http://192.168.56.1:3000/product/${product.id}`;
//     navigator.clipboard.writeText(productUrl).then(() => {
//       setShowCopyNotification(true);
//       setTimeout(() => setShowCopyNotification(false), 2500);
//     }).catch(() => {
//       const textarea = document.createElement('textarea');
//       textarea.value = productUrl;
//       document.body.appendChild(textarea);
//       textarea.select();
//       document.execCommand('copy');
//       document.body.removeChild(textarea);
//       setShowCopyNotification(true);
//       setTimeout(() => setShowCopyNotification(false), 2500);
//     });
//   };

//   const handleSaveToWishlist = () => {
//     const auth = checkAuthentication();
//     if (!auth.isAuthenticated) {
//       if (onOpenLoginModal) onOpenLoginModal();
//       else alert('Please log in to save to wishlist');
//       return;
//     }
//     try {
//       const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
//       if (!wishlist.includes(product.id)) {
//         wishlist.push(product.id);
//         localStorage.setItem('wishlist', JSON.stringify(wishlist));
//         alert('Added to wishlist!');
//       } else {
//         alert('Already in wishlist!');
//       }
//     } catch (error) {
//       console.error('Error saving to wishlist:', error);
//     }
//   };

//   const handleOpenRating = () => {
//     const auth = checkAuthentication();
//     if (!auth.isAuthenticated) {
//       if (onOpenLoginModal) onOpenLoginModal();
//       else alert('Please log in to rate this seller');
//       return;
//     }
//     setRatingSuccess('');
//     setRatingError('');
//     setShowRatingModal(true);
//   };

//   const handleSubmitRating = async () => {
//     if (userRating === 0) {
//       setRatingError('Please select a star rating');
//       return;
//     }
//     try {
//       setIsSubmittingRating(true);
//       setRatingError('');
//       const payload = { rating: userRating, comment: ratingComment.trim() };
//       console.log('Submitting rating:', payload);
//       let response;
//       if (typeof apiService.rateSeller === 'function') {
//         response = await apiService.rateSeller(product.seller, payload);
//       } else if (typeof apiService.submitRating === 'function') {
//         response = await apiService.submitRating(product.id, payload);
//       } else if (typeof apiService.createRating === 'function') {
//         response = await apiService.createRating(product.id, payload);
//       } else {
//         const auth = checkAuthentication();
//         const res  = await fetch(`/api/ads/${product.id}/rate/`, {
//           method: 'POST',
//           headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
//           body: JSON.stringify(payload),
//         });
//         response = await res.json();
//       }
//       if (response && (response.success || response.id || response.rating)) {
//         setRatingSuccess(`Thank you! You rated this seller ${userRating} star${userRating > 1 ? 's' : ''}.`);
//         setTimeout(() => {
//           setShowRatingModal(false);
//           setUserRating(0);
//           setRatingComment('');
//           setRatingSuccess('');
//         }, 2000);
//       } else {
//         setRatingError(response?.error || response?.detail || 'Failed to submit rating. Try again.');
//       }
//     } catch (error) {
//       console.error('Error submitting rating:', error);
//       setRatingError(error?.message || 'Failed to submit rating. Please try again.');
//     } finally {
//       setIsSubmittingRating(false);
//     }
//   };

//   // ── Report seller handlers ─────────────────────────────────────────────────
//   const handleOpenReport = () => {
//     const auth = checkAuthentication();
//     if (!auth.isAuthenticated) {
//       if (onOpenLoginModal) onOpenLoginModal();
//       else alert('Please log in to report a seller');
//       return;
//     }
//     setReportSuccess('');
//     setReportError('');
//     setReportReason('');
//     setReportDetails('');
//     setShowReportModal(true);
//   };

//   const handleSubmitReport = async () => {
//     if (!reportReason) {
//       setReportError('Please select a reason for reporting');
//       return;
//     }
//     try {
//       setIsSubmittingReport(true);
//       setReportError('');
//       const payload = {
//         reason: reportReason,
//         details: reportDetails.trim(),
//         seller: product.seller,
//         ad_id: product.id,
//       };
//       console.log('Submitting report:', payload);
//       const response = await apiService.reportSeller(product.id, payload);
//       if (response.success) {
//         setReportSuccess('Report submitted successfully. Our team will review it shortly.');
//         setTimeout(() => {
//           setShowReportModal(false);
//           setReportReason('');
//           setReportDetails('');
//           setReportSuccess('');
//         }, 2500);
//       } else {
//         setReportError(response.error || 'Failed to submit report. Please try again.');
//       }
//     } catch (error) {
//       console.error('Error submitting report:', error);
//       setReportError('An error occurred. Please try again.');
//     } finally {
//       setIsSubmittingReport(false);
//     }
//   };

//   return {
//     // Image
//     activeImageIndex, setActiveImageIndex,
//     // UI
//     showSellerInfo, showCopyNotification,
//     // Modals
//     showOfferModal, setShowOfferModal,
//     showMessageModal, setShowMessageModal,
//     showRatingModal, setShowRatingModal,
//     showReportModal, setShowReportModal,
//     // Like
//     likes, isLiked, isLiking,
//     // Offer
//     offerAmount, setOfferAmount,
//     offerMessage, setOfferMessage,
//     isSubmittingOffer, offerError, setOfferError,
//     // Message
//     messageText, setMessageText,
//     isSubmittingMessage, messageError, setMessageError,
//     // Rating
//     userRating, setUserRating,
//     hoverRating, setHoverRating,
//     ratingComment, setRatingComment,
//     isSubmittingRating, ratingSuccess, ratingError, setRatingError,
//     // Report
//     reportReason, setReportReason,
//     reportDetails, setReportDetails,
//     isSubmittingReport, reportSuccess, reportError, setReportError,
//     // User
//     sellerAvatarUrl, loggedInUser,
//     // Follow
//     isFollowing, followCount,
//     // Handlers
//     handleRelatedProductClick,
//     handleContactSellerClick,
//     handleLikeToggle,
//     handleMakeOffer,
//     handleSubmitOffer,
//     handleMessageSeller,
//     handleSubmitMessage,
//     handleCopyLink,
//     handleSaveToWishlist,
//     handleOpenRating,
//     handleSubmitRating,
//     handleFollowToggle,
//     handleOpenReport,
//     handleSubmitReport,
//     checkAuthentication,
//   };
// };

// export default useProductModal;